import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useScrolled } from '../../core/hooks/use-scrolled';
import { Language } from '../../core/i18n/constants';
import { useTranslatedLanguages } from '../../core/i18n/hooks/use-translated-languages';
import { AppHeaderButton } from '../app-header-button/app-header-button';
import { Button } from '../button/button';
import { LanguageSwitcher, LanguageSwitcherOption } from '../language-switcher/language-switcher';
import { usePathBuilder } from '../../core/routing/hooks/use-path-builder';
import { Badge } from '../badge/badge';
import { AppHeaderSwitchItems } from './app-header-switch-items/app-header-switch-items';
import styles from './app-header.module.scss';
import IgpLogo from './assets/igp-powder-coatings-logo.svg?react';

type Props = {
  isLoggedIn?: boolean;
  cartBadge?: React.ReactElement<typeof Badge>;
  currentLanguage: Language;
  handleLogin?: () => void;
  onSelectLocale: (language: Language) => void;
};

export const AppHeader: React.FC<Props> = ({
  isLoggedIn = false,
  cartBadge,
  currentLanguage,
  handleLogin,
  onSelectLocale,
}) => {
  const isScrolled = useScrolled(undefined, 50);
  const { t } = useTranslation();
  const { homePath, cartPath, profilePath } = usePathBuilder();
  const translatedLanguages = useTranslatedLanguages();

  const localeOptions: LanguageSwitcherOption[] = [
    { locale: Language.de, label: translatedLanguages.de },
    { locale: Language.en, label: translatedLanguages.en },
    { locale: Language.fr, label: translatedLanguages.fr },
    { locale: Language.pl, label: translatedLanguages.pl },
  ];

  return (
    <div className={classNames(styles.root, { [styles.isScrolled]: isScrolled })}>
      <div className={styles.innerContainer}>
        <Link aria-label={t('global.backToHome')} to={homePath()}>
          <IgpLogo className={styles.logo} />
        </Link>
        <AppHeaderSwitchItems />
        <div className={styles.pageMenuContainer}>
          {!isLoggedIn ? (
            <Button icon="box-half-arrow-in" variant="secondary" onClick={handleLogin}>
              {t('global.login')}
            </Button>
          ) : null}
          {isLoggedIn ? (
            <>
              <AppHeaderButton
                variant={{
                  badge: cartBadge,
                  type: 'linkIcon',
                  icon: 'cart-header',
                  label: t('cart.documentTitle'),
                  link: { to: cartPath() },
                }}
              />
              <AppHeaderButton
                variant={{
                  type: 'linkIcon',
                  icon: 'user-header',
                  label: t('profile.documentTitle'),
                  link: { to: profilePath() },
                }}
              />
            </>
          ) : null}
          <LanguageSwitcher
            isLoggedIn={isLoggedIn}
            options={localeOptions}
            selectedLocale={currentLanguage}
            onSelectLocale={onSelectLocale}
          />
        </div>
      </div>
    </div>
  );
};
